// Response schemas for the verification queue (plan §4 GET /verification/queue) and the result of
// a verify or send-back action. Unknown keys are dropped.
import { TASK_STATUSES } from '@cis/shared';
import { z } from 'zod';

const MAX_ID = 64;
const MAX_PAGE_SIZE = 100;

// Text the API shows: pack labels (titles, areas) are up to 500 characters, people's text up to
// 4,000, and team roles get Leads ids replaced by "[Leads only]", which can make a text about four
// times longer ("R1 " becomes "[Leads only] "). A tighter cap would make one long title or comment
// break the whole page, so the cap leaves room for that growth.
export const SHOWN_TEXT_MAX = 20_000;

const personSchema = z.object({
  id: z.number().int().positive(),
  displayName: z.string().max(SHOWN_TEXT_MAX),
});

export const queueRouteSchema = z.enum([
  'designated',
  'co_supervisor',
  'needs_independent_verifier',
]);

export const queueItemSchema = z.object({
  taskId: z.string().min(1).max(MAX_ID),
  title: z.string().max(SHOWN_TEXT_MAX),
  phase: z.string().max(MAX_ID),
  area: z.string().max(SHOWN_TEXT_MAX),
  due: z.string().nullable(),
  doneAt: z.string().nullable(),
  assignees: z.array(personSchema),
  route: queueRouteSchema,
  version: z.number().int().nonnegative(),
});

export const queuePageSchema = z.object({
  items: z.array(queueItemSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive().max(MAX_PAGE_SIZE),
  total: z.number().int().nonnegative(),
});

// A task action answers with the task page; the queue only needs the new status and version.
export const queueActionResultSchema = z.object({
  taskId: z.string().min(1).max(MAX_ID),
  state: z.object({
    status: z.enum(TASK_STATUSES),
    version: z.number().int().nonnegative(),
  }),
});

export type QueueRoute = z.infer<typeof queueRouteSchema>;
export type QueueItem = z.infer<typeof queueItemSchema>;
export type QueuePage = z.infer<typeof queuePageSchema>;
export type QueueActionResult = z.infer<typeof queueActionResultSchema>;
